<template>
  <b-form v-if="validations !== undefined" @submit.stop.prevent="onSubmit" novalidate>
    <div v-html="sanitizedContent(title)"></div>
    <div v-html="sanitizedContent(subtitle)"></div>
    <b-form-group v-for="item of visibleFormFields" :key="item.inputId" :id="`${item.inputId}-group`"
      :description="item.description" :label="item.label" :label-for="item.inputId">

      <!-- If type=date -->
      <b-input-group v-if="item.type === 'date'">
        <b-form-input :id="item.inputId" :name="item.inputName" :placeholder="item.placeholder" :disabled="item.disabled"
          v-model="$v.formModel[item.inputName].$model" type="text" autocomplete="off"
          :aria-describedby="`${item.inputId}-live-feedback`" :state="validateState(item.inputName)"></b-form-input>
        <b-form-invalid-feedback :id="`${item.inputId}-live-feedback`">
          <div v-for="(message, ruleKey) in validationRuleMessages?.[item.inputName]" :key="ruleKey"
            v-show="!$v.formModel[item.inputName][ruleKey]">
            {{ message }}
          </div>
        </b-form-invalid-feedback>
      </b-input-group>

      <!-- Else-If type=select -->
      <b-form-select v-else-if="item.type === 'select'" :id="item.inputId" :name="item.inputName"
        :placeholder="item.placeholder" v-model="$v.formModel[item.inputName].$model"
        :state="validateState(item.inputName)" :aria-describedby="`${item.inputId}-live-feedback`"
        :options="item.options"></b-form-select>

      <!-- Else-If type=radio -->
      <b-form-radio-group v-else-if="item.type === 'radio'" :id="item.inputId"
        v-model="$v.formModel[item.inputName].$model" :state="validateState(item.inputName)"
        :aria-describedby="`${item.inputId}-live-feedback`" :options="item.options"
        :name="item.inputName"></b-form-radio-group>

      <!-- Else-If type=signature -->
      <signature-pad v-else-if="item.type == 'signature'" :id="item.inputId" :name="item.inputName" @sign="getSign" />

      <!-- Else, render the input type as it is -->
      <b-form-input v-else :type="item.type" trim :id="item.inputId" :name="item.inputName" :disabled="item.disabled"
        :placeholder="item.placeholder" v-model="$v.formModel[item.inputName].$model"
        :state="validateState(item.inputName)" :aria-describedby="`${item.inputId}-live-feedback`">
      </b-form-input>

      <!-- Error Messages -->
      <b-form-invalid-feedback :id="`${item.inputId}-live-feedback`">
        <div v-for="(message, ruleKey) in validationRuleMessages?.[item.inputName]" :key="ruleKey"
          v-show="!$v.formModel[item.inputName][ruleKey]">
          {{ message }}
        </div>
      </b-form-invalid-feedback>
    </b-form-group>

    <b-row>
      <b-col>
        <b-button-group class="mt-5">
          <b-button class="default-button mr-2" @click="$emit('back')">{{ cancelFormButton.text }}</b-button>
          <b-button class="default-button" type="submit">{{ okFormButton.text }}</b-button>
          <loading :show="loading" />
        </b-button-group>
      </b-col>
    </b-row>
  </b-form>
</template>

<script>
import { marked } from 'marked'
import DOMPurify from 'dompurify'
import Validators, { buildValidator } from '@/utils/customFormValidations'
import Loading from '../Loading.vue'
import { msmApi, strapi } from '@/api'
import SignaturePad from './SignaturePad.vue'

const touchMap = new WeakMap()

export default {
  components: { Loading, SignaturePad },
  props: {
    formDefaultValues: Object,
    locale: String,
    isCRC: Boolean
  },
  data() {
    return {
      loading: false,
      form: [],
      formModel: {},
      validationRuleMessages: {},
      title: '',
      subtitle: '',
      okFormButton: {},
      cancelFormButton: {},
      validations: undefined
    }
  },
  created() {
    strapi
      .getSignatureForm(this.locale, this.isCRC)
      .then(([form, validationRules]) => {
        this.title = form.title
        this.subtitle = form.subtitle
        this.cancelFormButton = form.cancelFormButton
        this.okFormButton = form.okFormButton
        this.formModel = form.form
          .reduce((model, curr) =>
          (
            { ...model, [curr.inputName]: this.formDefaultValues[curr.inputName] || this.formDefaultValues[curr.defaultValue] || curr.defaultValue }),
            {}
          )
        this.form = form.form
        this.validationRuleMessages = validationRules
      })
      .then(() => {
        this.validations = Object
          .entries(this.validationRuleMessages)
          .reduce((validations, [inputName, rulesObj]) =>
          ({
            ...validations, [inputName]: Object.keys(rulesObj)
              .filter(key => key in Validators)
              .reduce((validators, key) => ({
                ...validators,
                [key]: buildValidator(key, this.form.find(i => i.inputName === inputName), this.formModel)
              })
                , {}
              )
          }), {}
          )
      })
  },
  computed: {
    visibleFormFields() {
      return this.form.filter(
        item => {
          if (!item.dependOnFieldId) {
            return !item.hidden
          }
          // TODO: check with Tom
          const values = item.dependOnFieldValue.split(',')
          return values.some(v => this.$v.formModel[item.dependOnFieldId]?.$model?.includes(v))
        }
      )
    }
  },
  validations() {
    return {
      formModel: this.validations || {}
    }
  },
  methods: {
    onContext(ctx) {
      this.formatted = ctx.selectedFormatted
      this.selected = ctx.selectedYMD
    },
    sanitizedContent(content) {
      return DOMPurify.sanitize(marked(content))
    },
    validateState(name) {
      const fieldModel = this.$v.formModel[name]
      return fieldModel?.$dirty ? !fieldModel?.$error : null
    },
    delayTouch($v) {
      $v.$reset()
      if (touchMap.has($v)) {
        clearTimeout(touchMap.get($v))
      }
      touchMap.set($v, setTimeout($v.$touch, 500))
    },
    getSign(inputName, signInfo) {
      const { isEmpty, data } = signInfo
      this.$v.formModel[inputName].$model = isEmpty ? null : data
    },
    onSubmit() {
      this.$v.formModel.$touch()

      if (this.$v.formModel.$anyError) {
        return
      }

      this.loading = true
      if (this.isCRC === false) {
        msmApi
          .signatureForm({ ...this.formModel, ...this.formDefaultValues }).then(() => {
            this.$emit('submit', this.formModel)
          })
          .catch((error) => {
            this.$gtag.exception({
              description: JSON.stringify(error),
              fatal: true
            })
          })
          .finally(() => {
            this.loading = false
          })
      } else {
        this.$emit('submit', this.formModel)
        this.loading = false
      }
    }
  }
}
</script>
